import type { PortalProductEntry } from '@features/products/types';
import { actionLabel } from '@features/products/ui/productCopy';

import { formatCurrency } from '../utils/guestRideUtils';

// The presentational read of the Guest Rides credit decision. Pure on purpose: precedence
// and copy are the parts most likely to drift, and this app's jest defaults to the node
// environment, so keeping them out of the components makes them testable without a DOM
// (the same split features/products/ui/productCopy.ts already uses).
//
// WHAT ARRIVES HERE. The algorithm builds context as `Audienced<T>` values, but the
// envelope builder unwraps them before the envelope leaves the SDK
// (packages/products/src/features/product-access/envelope.ts, `[key, entry.value]`), and
// the getProductAccess action carries envelope internals as jsonb. So this module sees
// plain values, each key individually absent when the decision could not compute it:
//
//   outstandingBalance?: number      creditLimit?: number      remainingCredit?: number
//   nextSettlementDate?: string      creditWarning?: { threshold, thresholdAmount }
//   paymentFailureType?: 'payment_failed'
//
// Because it is jsonb the whole way down, every field is validated here rather than
// trusted. A field this module cannot read renders nothing at all: no NaN, no "$undefined",
// no placeholder dash. The portal never reconstructs a limit, a threshold or a balance.

/** The reason keys this surface has credit-specific copy for. Anything else is #1315's
 *  generic notice's job. */
export const CREDIT_REASON_KEYS = ['credit_limit_exceeded', 'payment_failed', 'missing_payment_method'] as const;

/** Program ground rule 4: settlement copy is always "on or after" a date, never a weekday
 *  and never a promise of a specific run. */
export const SETTLEMENT_PREFIX = 'on or after';

export interface CreditMeterView {
  outstandingBalance: number;
  creditLimit: number;
  /** The decision's own figure, or null when it omitted one. Never derived from the pair
   *  above: the decision floors it at 0 and is the authority. */
  remainingCredit: number | null;
  /** Balance over limit, clamped to 0..1. For the bar's width only, never for copy. */
  usedFraction: number;
  /** The marker's fraction of the limit, so the bar can mark where the warning sits.
   *  null when no marker rides. */
  warningThreshold: number | null;
}

/** A recovery step resolved onto one of the portal's existing flows. */
export interface CreditActionView {
  type: string;
  label: string;
  route: string;
}

export interface CreditNoticeView {
  severity: 'warning' | 'error';
  title: string;
  body: string;
  /** The "on or after" line, or null when the decision carries no settlement date. */
  settlementLine: string | null;
  actions: CreditActionView[];
}

export interface GuestRidesCreditView {
  meter: CreditMeterView | null;
  notice: CreditNoticeView | null;
  /** True when the decision is limited or read_only for a reason this surface has no
   *  credit copy for, so the page should render #1315's generic ProductAccessNotice
   *  instead. Exactly one banner renders either way. */
  deferToGenericNotice: boolean;
}

const EMPTY: GuestRidesCreditView = { meter: null, notice: null, deferToGenericNotice: false };

/** A finite number, or null. Rejects the strings, nulls and NaNs jsonb can deliver. */
function readNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function readWarningMarker(value: unknown): { threshold: number; thresholdAmount: number } | null {
  if (typeof value !== 'object' || value === null) return null;
  const marker = value as Record<string, unknown>;
  const threshold = readNumber(marker.threshold);
  const thresholdAmount = readNumber(marker.thresholdAmount);
  if (threshold === null || thresholdAmount === null) return null;
  return { threshold, thresholdAmount };
}

/**
 * The settlement instant as a plain calendar date. Formatted in UTC so the rendered date
 * is the period boundary the SDK derived rather than a neighbouring day in the reader's
 * zone, and with no weekday token, which ground rule 4 forbids.
 */
export function formatSettlementDate(value: unknown, month: 'long' | 'short' = 'long'): string | null {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Intl.DateTimeFormat('en-US', {
    month,
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(parsed);
}

function settlementLine(context: Record<string, unknown>): string | null {
  const date = formatSettlementDate(context.nextSettlementDate);
  return date === null ? null : `Scheduled billing collects this balance ${SETTLEMENT_PREFIX} ${date}.`;
}

/** Semantic actions resolved onto the host's existing flows, through the host's own route map.
 *  An action the host has no route for is dropped: a button that goes nowhere is worse than one
 *  fewer button. The embed has no invoices or billing-preferences page, so its map is empty and
 *  every recovery button drops out. */
function resolveActions(types: readonly string[], routes: Readonly<Record<string, string>>): CreditActionView[] {
  return types
    .filter(type => routes[type] !== undefined)
    .map(type => ({ type, label: actionLabel(type), route: routes[type] }));
}

/** Dealer copy per blocked reason. Distinct per key: "paused" alone does not tell a dealer
 *  what to do next, and each of the three has a different next step. */
const BLOCKED_BODY: Record<string, string> = {
  credit_limit_exceeded:
    'Your outstanding guest rides balance has reached the credit limit for this rooftop. Existing rides are unaffected, and you can still track and manage them.',
  payment_failed:
    'Your most recent guest rides payment did not go through. New rides turn back on once a payment clears.',
  missing_payment_method:
    'Automatic billing is on for this rooftop, but there is no payment method on file. Add one to start requesting rides again.',
};

const BLOCKED_TITLE = 'New guest rides are paused';

function buildMeter(context: Record<string, unknown>): CreditMeterView | null {
  const outstandingBalance = readNumber(context.outstandingBalance);
  const creditLimit = readNumber(context.creditLimit);
  // The meter is a balance against a limit. Without both there is nothing to draw, and
  // half a meter is worse than none.
  if (outstandingBalance === null || creditLimit === null) return null;

  // A zero or negative limit cannot be divided by. The decision normalises those to "no
  // credit gating" upstream, so this only guards a shape that should not arrive.
  const rawFraction = creditLimit > 0 ? outstandingBalance / creditLimit : 1;

  return {
    outstandingBalance,
    creditLimit,
    remainingCredit: readNumber(context.remainingCredit),
    usedFraction: Math.min(1, Math.max(0, rawFraction)),
    warningThreshold: readWarningMarker(context.creditWarning)?.threshold ?? null,
  };
}

function buildWarningNotice(
  context: Record<string, unknown>,
  meter: CreditMeterView | null,
  routes: Record<string, string>,
): CreditNoticeView | null {
  const marker = readWarningMarker(context.creditWarning);
  // PRESENCE is the signal. The marker is absent below the threshold and is never `false`,
  // so the portal never compares a balance against a threshold itself.
  if (marker === null) return null;

  const body =
    meter === null
      ? `Your outstanding guest rides balance has passed ${formatCurrency(marker.thresholdAmount)}. New rides pause when it reaches the credit limit.`
      : `You have used ${formatCurrency(meter.outstandingBalance)} of your ${formatCurrency(meter.creditLimit)} guest rides credit limit. New rides pause when the balance reaches the limit.`;

  return {
    severity: 'warning',
    title: 'You are close to your guest rides credit limit',
    body,
    settlementLine: settlementLine(context),
    // An available decision carries no actions of its own, so the banner offers the same
    // existing invoices flow the generic notice routes `pay_now` to.
    actions: resolveActions(['pay_now'], routes),
  };
}

/**
 * Read one Guest Rides product entry into what the credit surfaces should render.
 *
 * Precedence: a blocked credit decision outranks the warning marker. The marker still
 * rides a blocked decision by contract, and one condition must not produce two banners.
 */
export function readGuestRidesCredit(
  entry: PortalProductEntry | undefined,
  actionRoutes: Readonly<Record<string, string>>,
): GuestRidesCreditView {
  if (!entry) return EMPTY;

  const context = (entry.context ?? {}) as Record<string, unknown>;
  const meter = buildMeter(context);
  const reasonKey = entry.reason?.key;
  const isBlockedState = entry.state === 'limited' || entry.state === 'read_only';
  const hasCreditCopy =
    entry.state === 'limited' &&
    reasonKey !== undefined &&
    (CREDIT_REASON_KEYS as readonly string[]).includes(reasonKey);

  if (hasCreditCopy) {
    return {
      meter,
      notice: {
        severity: 'error',
        title: BLOCKED_TITLE,
        // `paymentFailureType` selects nothing here: the reason key already identifies the
        // branch, and the fixed string it carries is never rendered as prose.
        body: BLOCKED_BODY[reasonKey as string],
        settlementLine: settlementLine(context),
        actions: resolveActions((entry.actions ?? []).map(action => action.type), actionRoutes),
      },
      deferToGenericNotice: false,
    };
  }

  if (isBlockedState) {
    return { meter, notice: null, deferToGenericNotice: true };
  }

  return { meter, notice: buildWarningNotice(context, meter, actionRoutes), deferToGenericNotice: false };
}

/**
 * True when the decision pauses new rides for a reason this surface has credit copy for. The
 * Guest Rides layout then lets the credit panel present the state in place of the shell's
 * generic state banner, so the dealer still sees exactly one.
 */
export function isCreditBlocked(entry: PortalProductEntry | undefined): boolean {
  const reasonKey = entry?.reason?.key;
  return (
    entry?.state === 'limited' &&
    reasonKey !== undefined &&
    (CREDIT_REASON_KEYS as readonly string[]).includes(reasonKey)
  );
}

/**
 * How far the balance sits past the limit, in cents-rounded dollars, for the blocked panel's
 * copy. Null at or under the limit: a dealer paused exactly at the limit is told they reached
 * it, not that they are $0.00 over.
 */
export function overLimitBy(meter: CreditMeterView | null): number | null {
  if (meter === null) return null;
  const over = Math.round((meter.outstandingBalance - meter.creditLimit) * 100) / 100;
  return over > 0 ? over : null;
}
